import React from 'react';
import uniq from 'lodash.uniq';
import Image from 'next/image';
import parse, { attributesToProps, domToReact } from 'html-react-parser';
import dayjs from 'dayjs';
import Lives from 'components/lives';

const ArticleDetail = ({ article, summary, ip }) => {
  const contentStr = article.content
    ? article.content
      .replace(/src="https?:/gi, 'src="')
      .replace(/style=".*?"/gi, '')
      .replace(/<font.*?>/gi, '')
      .replace(/<\/font>/gi, '')
      .replace(/h1/gi, 'h2')
      .replace(/<script.*?<\/script>/gi, '')
      .replace(/<noscript.*?<\/noscript>/gi, '')
      .replace(/<style.*?<\/style>/gi, '')
      .replace(/<p>https?:\/\/.*?>/gi, '')
      .replace(
        /\[bzplayer mp4=(.*?)\]/gi,
        (match, string) => `<video preload="none"><source src=${string} type="video/mp4"></source></video>`,
      )
    : '';

  const replace = ({ attribs, name, ...rest }) => {
    if (!name) return false;

    // for getfappy.com
    if (attribs && attribs.class && attribs.class.includes('flowplayer') && attribs['data-item']) {
      const dataItem = JSON.parse(attribs['data-item']);
      return (
        <video preload="none">
          {dataItem.sources.map((source) => (
            <source src={source.src} key={source.src} type={source.type} />
          ))}
          This browser does not support the HTML5 video element.
        </video>
      );
    }

    // for thotslife.com
    if (name === 'figure' && attribs.class === 'mace-gallery-teaser') {
      let gallery = [];
      try {
        gallery = JSON.parse(attribs['data-g1-gallery'] || '[]');
      } catch (e) {
        // console.log(e);
      }

      if (gallery.length) {
        const galleryList = gallery.map((item) => {
          if (item.type === 'image') {
            return (
              <span className="d-block image-container my-3">
                <Image
                  className="image"
                  src={item.full}
                  key={item.id}
                  title={item.title}
                  placeholder="blur"
                  blurDataURL="data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='500px' height='500px' viewBox='0 0 500 500' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Crect id='Rectangle' fill='%23EFEFEF' x='0' y='0' width='500' height='500' rx='8'%3E%3C/rect%3E%3Ctext id='leakedonlyfans' font-family='Helvetica Neue' font-size='20' font-weight='500' letter-spacing='0' fill='%23CCCCCC'%3E%3Ctspan x='156' y='240'%3ELEAKED ONLYFANS%3C/tspan%3E%3C/text%3E%3C/g%3E%3C/svg%3E"
                  layout="fill"
                  unoptimized
                />
              </span>
            );
          }

          if (item.type === 'ad') {
            /* eslint-disable react/no-array-index-key */
            return null;
            /* eslint-enable */
          }
          return null;
        });

        return <div className="gallery-list">{galleryList}</div>;
      }

      return null;
    }

    if (name === 'img' && attribs && attribs.src) {
      // eslint-disable-next-line
      const { src, alt, srcset } = attribs;
      const dataSrcset = attribs['data-srcset'];
      const dataSrc = attribs['data-src'];

      return (
        <span className="d-block image-container my-3">
          <Image
            className="image"
            src={dataSrc || src}
            srcSet={dataSrcset || srcset}
            placeholder="blur"
            blurDataURL="data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='500px' height='500px' viewBox='0 0 500 500' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Crect id='Rectangle' fill='%23EFEFEF' x='0' y='0' width='500' height='500' rx='8'%3E%3C/rect%3E%3Ctext id='leakedonlyfans' font-family='Helvetica Neue' font-size='20' font-weight='500' letter-spacing='0' fill='%23CCCCCC'%3E%3Ctspan x='156' y='240'%3ELEAKED ONLYFANS%3C/tspan%3E%3C/text%3E%3C/g%3E%3C/svg%3E"
            alt={article.title}
            title={article.title}
            layout="fill"
            unoptimized
          />
        </span>
      );
    }

    if (name === 'a') {
      const props = attributesToProps(attribs);
      const nofollowProps = {
        ...props,
        rel: 'nofollow noreferrer noopener',
        target: '_blank',
      };
      return (
        <a {...nofollowProps}>
          {domToReact(rest.children, {
            trim: true,
            replace,
          })}
        </a>
      );
    }

    if (name === 'video') {
      const props = attributesToProps(attribs);
      delete props.poster;
      delete props.preload;
      delete props.height;
      delete props.width;
      return (
        <video {...props} preload="none">
          {domToReact(rest.children, {
            trim: true,
            replace,
          })}
        </video>
      );
    }

    return false;
  };

  const contentDom = parse(contentStr, {
    trim: true,
    replace,
  });

  const date = dayjs(article.created_at).format('YYYY-MM-DD HH:mm');

  const tagsArr = uniq(
    article.title
      .replace(/\(.*?\)/g, '')
      .trim()
      .split(' ')
      .filter((item) => item.length > 1),
  );

  return (
    <>
      <h1 className="h3">{article.title}</h1>
      <p className="article-meta my-2">
        <span className="author">
          By
          {article.source || ' Admin'}
        </span>
        <span className="ml-2 mr-1">·</span>
        <time dateTime={article.created_at} className="time">
          {date}
        </time>
      </p>

      {article.cover ? (
        <div className="mw-100 my-3">
          <Image
            height={700}
            width={700}
            src={article.cover}
            alt={article.title}
            title={article.title}
            objectFit="cover"
            unoptimized
            priority
          />
        </div>
      ) : null}

      <h2 className="d-none">
        Watch
        {` ${article.title} `}
        Online and Free
      </h2>

      <div className="article">
        <p className="my-3">{summary}</p>
        <p className="my-3 font-weight-bold">
          <a
            href="https://bit.ly/3llyMF5"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="font-weight-bold"
          >
            &gt;&gt; Free Adult Webcams, Live Sex NOW &lt;&lt;
          </a>
        </p>

        <p className="my-3">
          👉👉 Get a FREE OnlyFans Premium Account:{' '}
          <a
            href="https://hackkit.xyz/lp/onlyFans-premium-account-generator/?utm_source=leaked-onlyfans.xyz&utm_content=before-content"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="font-weight-bold"
          >
            OnlyFans Premium Membership Generator
          </a>
        </p>

        {contentDom}

        <p className="my-3">
          👉👉 Get a FREE OnlyFans Premium Account:{' '}
          <a
            href="https://hackkit.xyz/lp/onlyFans-premium-account-generator/?utm_source=leaked-onlyfans.xyz&utm_content=before-content"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="font-weight-bold"
          >
            OnlyFans Premium Membership Generator
          </a>
        </p>

        <Lives ip={ip} size={4} />

        <p className="my-4 font-weight-bold text-center">
          <a
            href="https://bit.ly/3llyMF5"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="font-weight-bold btn btn-danger btn-lg text-white cta-btn"
          >
            &gt;&gt; Free Adult Webcams, ACCESS NOW &lt;&lt;
          </a>
        </p>
      </div>

      {tagsArr.length ? (
        <p className="article-tags my-3">
          {tagsArr.map((tag) => (
            <span key={tag} className="article-tag mr-3 px-3 py-2 mb-3">
              #{tag}
            </span>
          ))}
        </p>
      ) : null}
    </>
  );
};

export default ArticleDetail;
